import * as React from 'react';
import {
  StackLayout,
  HeadingText,
  Box,
  Button,
  Modal,
  styled,
  focusOutlineOuter,
  VisuallyHidden,
  Tooltip,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { useUniqueId } from '@leagueplatform/web-common';

// Styled components for the card container and zoom button
export const CardContainer = styled(Box, {
  position: 'relative',
  display: 'inline-flex',
  '&:hover .zoom-button, &:focus-within .zoom-button': {
    opacity: 1,
  },
});

export const ZoomButton = styled(Button, {
  position: 'absolute',
  top: '$one',
  insetInlineEnd: '$oneAndHalf',
  opacity: 0,
  zIndex: 1,

  '&:focus': {
    opacity: 1,
  },
});

const ZoomedCardContainer = styled(Box, {
  padding: '$one',
  overflow: 'auto',
  '&[class*="GDS"]:focus': {
    outline: 'none',
  },
  '&[class*="GDS"]:focus-visible': {
    ...focusOutlineOuter,
  },
  img: {
    transform: 'scale(var(--card-scale, 1))',
    transformOrigin: 'left top',
    transition: 'transform 0.3s ease',
  },
});

interface ZoomModalProps {
  cardType: 'front' | 'back';
  onZoomClick: (event: React.SyntheticEvent) => void;
  title: string;
  triggerLabelId?: string;
}

const MAX_ZOOM_SCALE = 3;
const MIN_ZOOM_SCALE = 1;
const ZOOM_STEP = 0.25;

// Helper function to format zoom level as percentage
const formatZoomPercentage = (zoomScale: number) =>
  `${Math.round(zoomScale * 100)}%`;

export function ZoomCardModal({
  cardType,
  title,
  children,
  onZoomClick,
  triggerLabelId,
}: React.PropsWithChildren<ZoomModalProps>) {
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = React.useState(false);
  const [scale, setScale] = React.useState(1);
  const [isFirstRender, setIsFirstRender] = React.useState(true);
  const zoomModalTriggerRef = React.useRef<HTMLButtonElement>(null);
  const zoomModalId = useUniqueId({ prefix: 'zoom-modal' });
  const zoomButtonLabelId = `${zoomModalId}-button-label`;
  const cardFaceId = `${zoomModalId}-card-face`;
  const modalTitleId = `${zoomModalId}-title`;
  const zoomModalDescriptionId = `${zoomModalId}-description`;
  const cardFace =
    cardType === 'front'
      ? formatMessage({ id: 'BENEFITS_CARD_FACE_FRONT' })
      : formatMessage({ id: 'BENEFITS_CARD_FACE_BACK' });

  const handleOpenChange = React.useCallback((open: boolean) => {
    setIsOpen(open);
    setIsFirstRender(false);
  }, []);

  const handleZoomClick = React.useCallback(
    (event: React.SyntheticEvent) => {
      setIsOpen(true);
      onZoomClick(event);
    },
    [onZoomClick],
  );

  const handleZoomIn = React.useCallback(() => {
    setScale((prevScale) => {
      const newScale = Math.min(prevScale + ZOOM_STEP, MAX_ZOOM_SCALE);
      return newScale;
    });
  }, []);

  const handleZoomOut = React.useCallback(() => {
    setScale((prevScale) => {
      const newScale = Math.max(prevScale - ZOOM_STEP, MIN_ZOOM_SCALE);
      return newScale;
    });
  }, []);

  const handleResetZoom = React.useCallback(() => {
    setScale(MIN_ZOOM_SCALE);
  }, []);

  // Reset scale when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setScale(MIN_ZOOM_SCALE);
    }
    if (!isOpen && zoomModalTriggerRef.current && !isFirstRender) {
      zoomModalTriggerRef.current.focus();
    }
  }, [isOpen, zoomModalTriggerRef, isFirstRender]);

  const zoomStatus = React.useMemo(() => formatZoomPercentage(scale), [scale]);
  const isZoomed = scale > MIN_ZOOM_SCALE;
  const isMaxZoomed = scale >= MAX_ZOOM_SCALE;

  return (
    <Modal.Root open={isOpen} onOpenChange={handleOpenChange}>
      <CardContainer>
        {children}
        <Tooltip
          content={formatMessage({ id: 'BENEFITS_ZOOM_LABEL' })}
          aria-hidden
        >
          <ZoomButton
            ref={zoomModalTriggerRef}
            className="zoom-button"
            priority="secondary"
            size="small"
            icon="interfaceSearch"
            hideLabel
            onClick={handleZoomClick}
            aria-labelledby={`${zoomButtonLabelId} ${
              triggerLabelId ?? ''
            } ${cardFaceId}`}
            // Tooltip adds aria-describedby to the button, so we need to
            // override it to avoid duplicate descriptions.
            aria-describedby={undefined}
            css={{
              backgroundColor: '$surfaceBackgroundPrimary',
            }}
          >
            <span id={zoomButtonLabelId}>
              {formatMessage({ id: 'BENEFITS_ZOOM_LABEL' })}
            </span>
            <span id={cardFaceId}>{cardFace}</span>
          </ZoomButton>
        </Tooltip>
      </CardContainer>
      <Modal.Content
        padding="$none $oneAndHalf $oneAndHalf"
        css={{ position: 'relative' }}
        aria-labelledby={modalTitleId}
        aria-describedby={undefined}
      >
        <Modal.Title id={modalTitleId} asChild>
          <HeadingText level="2" size="lg">
            {title}
          </HeadingText>
        </Modal.Title>

        {/* Hidden instructions for screen readers */}
        <Modal.Description aria-hidden id={zoomModalDescriptionId}>
          {formatMessage({ id: 'BENEFITS_ZOOM_MODAL_INSTRUCTIONS' })}
        </Modal.Description>

        <StackLayout
          spacing="$half"
          css={{ alignItems: 'center', paddingBlockStart: '$three' }}
        >
          <ZoomedCardContainer
            css={{ '--card-scale': scale }}
            as="section"
            aria-labelledby={modalTitleId}
            aria-describedby={zoomModalDescriptionId}
            tabIndex={0}
          >
            {children}
          </ZoomedCardContainer>
          <Box
            css={{
              display: 'flex',
              gap: '$quarter',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '$card',
            }}
          >
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
                gap: '$oneAndHalf',
                padding: '$one $oneAndHalf',
                borderInlineEnd: '$onSurfaceBorderSubdued',
                borderInlineEndWidth: '$borderWidth',
                borderInlineEndStyle: 'solid',
              }}
            >
              <Tooltip
                content={formatMessage({ id: 'BENEFITS_ZOOM_RESET_LABEL' })}
                aria-hidden
                css={{
                  // Tooltip z-index in the theme is lower than the modal z-index.
                  zIndex: '$modal',
                }}
              >
                <Button
                  priority="secondary"
                  icon="interfaceRefresh"
                  hideLabel
                  onClick={handleResetZoom}
                  disabled={!isZoomed}
                  aria-describedby={undefined}
                >
                  {formatMessage({ id: 'BENEFITS_ZOOM_RESET_LABEL' })}
                </Button>
              </Tooltip>
            </Box>
            <Box
              css={{
                display: 'flex',
                alignItems: 'center',
                gap: '$oneAndHalf',
                padding: '$one $oneAndHalf',
              }}
            >
              <Tooltip
                content={formatMessage({ id: 'BENEFITS_ZOOM_OUT_LABEL' })}
                aria-hidden
                css={{
                  // Tooltip z-index in the theme is lower than the modal z-index.
                  zIndex: '$modal',
                }}
              >
                <Button
                  priority="secondary"
                  icon="interfaceMinus"
                  hideLabel
                  onClick={handleZoomOut}
                  disabled={!isZoomed}
                  aria-describedby={undefined}
                >
                  {formatMessage({ id: 'BENEFITS_ZOOM_OUT_LABEL' })}
                </Button>
              </Tooltip>
              <Box
                aria-live="polite"
                css={{ display: 'flex', alignItems: 'center' }}
              >
                <span aria-hidden>{zoomStatus}</span>
                <VisuallyHidden>
                  {formatMessage(
                    { id: 'BENEFITS_ZOOM_STATUS' },
                    { zoom: zoomStatus },
                  )}
                </VisuallyHidden>
              </Box>
              <Tooltip
                content={formatMessage({ id: 'BENEFITS_ZOOM_IN_LABEL' })}
                aria-hidden
                css={{
                  // Tooltip z-index in the theme is lower than the modal z-index.
                  zIndex: '$modal',
                }}
              >
                <Button
                  priority="secondary"
                  icon="interfacePlus"
                  hideLabel
                  onClick={handleZoomIn}
                  disabled={isMaxZoomed}
                  aria-describedby={undefined}
                >
                  {formatMessage({ id: 'BENEFITS_ZOOM_IN_LABEL' })}
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </StackLayout>
      </Modal.Content>
    </Modal.Root>
  );
}
